/**
 * The floating clone shown while dragging. Portal-to-`body` so no ancestor
 * `overflow:hidden` clips it; anchored at the source element's start rect and
 * moved imperatively by `DndContext` (rAF follow for pointer, snap for
 * keyboard). The consumer supplies the visual via the render-prop child.
 *
 * Renders nothing when idle. The pointer follow writes `transform` straight onto
 * this node, so it never re-renders per frame.
 */
import { useEffect, useState, type CSSProperties, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useDndContext } from './DndContext.tsx'
import type { DndData } from './types.ts'

export interface DragOverlayProps {
  children: (activeData: DndData | null) => ReactNode
  className?: string
}

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false)
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const update = () => setReduced(mq.matches)
    update()
    mq.addEventListener('change', update)
    return () => mq.removeEventListener('change', update)
  }, [])
  return reduced
}

export function DragOverlay({ children, className }: DragOverlayProps) {
  const ctx = useDndContext()
  const reducedMotion = usePrefersReducedMotion()

  if (ctx.status !== 'dragging' || ctx.activeRect === null) return null
  if (typeof document === 'undefined') return null

  const style: CSSProperties = {
    position: 'fixed',
    top: ctx.activeRect.top,
    left: ctx.activeRect.left,
    width: ctx.activeRect.width,
    height: ctx.activeRect.height,
    pointerEvents: 'none',
    zIndex: 50,
    // Keyboard moves snap between buckets — animate that, unless the user opts
    // out. Pointer follow is continuous, so no transition.
    transition: ctx.mode === 'keyboard' && !reducedMotion ? 'transform 150ms ease' : undefined,
  }

  return createPortal(
    <div ref={ctx.setOverlayNode} style={style} className={className} aria-hidden="true">
      {children(ctx.activeData)}
    </div>,
    document.body,
  )
}
